/**
 * CapGate — page-level capability gate for a whole route.
 *
 * The backend is already authoritative (every endpoint is behind `require_cap`
 * or an explicit grant check), but without this a user who types a URL still
 * got the page shell and a wall of 403 toasts, which reads like the module is
 * open to everyone. Wrap the route element instead:
 *
 *   <Route path="gsoc/new" element={<CapGate module="gsoc" action="add"><GSOCEntry /></CapGate>} />
 *
 * Besides a module capability, a route can be opened by an HR grant or a role
 * (OR semantics), to mirror a menu item gated the same way — e.g. Payroll
 * Review is the `payroll` grant plus Finance and admins.
 *
 * Children render while permissions are still resolving, so a permitted user
 * never sees a flash of the denied panel.
 */
import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import AccessState from './AccessState';

export default function CapGate({ module, action = 'view', hrGrant, roles, children }) {
  const { user } = useAuth();
  const perms = useEffectivePermissions();
  if (!perms.loaded) return children;
  const role = user?.role;
  const allowed =
    role === 'admin' ||
    (module && perms.can(module, action)) ||
    (hrGrant && perms.hasGrant(hrGrant)) ||
    (Array.isArray(roles) && roles.includes(role));
  if (allowed) return children;
  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6" data-testid={`cap-gate-denied-${module || hrGrant || 'access'}`}>
      <AccessState module={module || 'this area'} action={action} items={[]} denied />
    </div>
  );
}
